import React from 'react';
import { TimeSeriesChart } from '../charts/TimeSeriesChart';
import { TimeSeriesTypeToggle } from '../charts/TimeSeriesTypeToggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { PanelStateMessage } from './PanelStateMessage';
import { formatMoney } from '../../lib/formatMoney';
import { COMPACT_CHART_HEIGHT } from './panelSizes';
import { useTheme } from '../../context/ThemeContext';
import { usePersistedState } from '../../hooks/usePersistedState';

function SkeletonChart({ compact = false }) {
  return (
    <div className={`${compact ? COMPACT_CHART_HEIGHT : 'h-[260px]'} w-full animate-pulse rounded bg-slate-200`} />
  );
}

function EmptyState({ compact = false }) {
  return (
    <div className={`flex ${compact ? COMPACT_CHART_HEIGHT : 'h-[260px]'} items-center justify-center text-sm text-slate-400`}>
      No revenue data found for this period
    </div>
  );
}

export function RevenueTrendPanel({ revenueByDate, loading, error, variant = 'detailed', currencyCode = 'DKK' }) {
  const { chartLine } = useTheme();
  const [seriesType, setSeriesType] = usePersistedState('vi.chart.revenueSeriesType.v2', 'column');
  const isCompact = variant === 'compact';
  const chartHeight = isCompact ? 210 : 260;

  if (error) {
    return (
      <Card className="lg:col-span-2 overflow-hidden border-slate-300">
        <CardHeader className="px-3 py-2.5">
          <CardTitle>Revenue Over Time</CardTitle>
          <CardDescription>Daily revenue from real invoice data</CardDescription>
        </CardHeader>
        <CardContent className="px-2.5 pb-2.5 pt-2">
          <PanelStateMessage
            variant="error"
            title="Unable to load revenue trend"
            message={error?.message || 'Try adjusting the filters or retrying the request.'}
          />
        </CardContent>
      </Card>
    );
  }

  const rawPoints = (revenueByDate || [])
    .filter((item) => item.date)
    .map((item) => ({
      date: item.date,
      value: item.totalRevenue || 0,
      totalRevenue: item.totalRevenue || 0,
      invoiceCount: item.invoiceCount || 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const totalRevenue = rawPoints.reduce((sum, d) => sum + d.value, 0);
  const peakDay = rawPoints.length
    ? rawPoints.reduce((max, d) => (d.value > max.value ? d : max), rawPoints[0])
    : null;
  const avgRevenue = rawPoints.length ? Math.round(totalRevenue / rawPoints.length) : 0;

  const summaryItems = [
    {
      label: 'Peak day',
      value: peakDay ? peakDay.date.slice(5) : '—',
      detail: peakDay ? formatMoney(peakDay.value, { currency: currencyCode }) : '—',
    },
    {
      label: 'Daily avg',
      value: formatMoney(avgRevenue, { currency: currencyCode }),
      detail: `${rawPoints.length} days`,
    },
    {
      label: 'Total',
      value: formatMoney(totalRevenue, { currency: currencyCode }),
      detail: `${rawPoints.reduce((s, d) => s + d.invoiceCount, 0)} invoices`,
    },
  ];

  return (
    <Card className="flex h-full w-full min-h-0 flex-col overflow-hidden border-slate-300 lg:col-span-2">
      <CardHeader className="px-3 py-2.5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1 text-left">
            <CardTitle>Revenue Over Time</CardTitle>
            <CardDescription>Daily revenue from real invoice data</CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <TimeSeriesTypeToggle value={seriesType} onChange={setSeriesType} />
            <div className="flex items-center gap-2 whitespace-nowrap text-[10px] text-slate-500">
              <span className="h-1.5 w-1.5 rounded-none" style={{ backgroundColor: chartLine.line }} />
              <span>Revenue ({currencyCode})</span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col px-2.5 pb-2.5 pt-2">
        <div className="flex min-h-0 flex-1 flex-col space-y-2">
          <div className={`grid grid-cols-1 gap-2 sm:grid-cols-3 ${isCompact ? 'hidden' : ''}`}>
            {summaryItems.map((item) => (
              <div
                key={item.label}
                className="border border-slate-200 bg-white/70 px-3 py-2 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset]"
              >
                <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500">{item.label}</div>
                <div className="mt-1 flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold tabular-nums text-[#201C35]">{item.value}</span>
                  <span className="text-[10px] text-slate-500">{item.detail}</span>
                </div>
              </div>
            ))}
          </div>

          {loading ? (
            <SkeletonChart compact={isCompact} />
          ) : rawPoints.length === 0 ? (
            <EmptyState compact={isCompact} />
          ) : (
            <>
              <TimeSeriesChart
                id="revenue-trend-chart"
                rawPoints={rawPoints}
                valueKey="totalRevenue"
                valueLabel="Revenue"
                currencyCode={currencyCode}
                height={chartHeight}
                seriesType={seriesType}
                ariaLabel="Revenue over time chart"
              />
              <div id="revenue-trend-announcer" className="sr-only" aria-live="polite" />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
